import React, { useState, useEffect } from 'react'
import { Modal, Button, Grid, Form, Input, Table, Icon } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import classNames from 'classnames'
import _ from 'lodash'
import CustomCalendarInput from '../../../CustomCalendarInput/CustomCalendarInput'
import { maskTextToExtractFloat } from '../../../../utils/format'
import { allowAddTransitStockArrival } from './validationForm'
import { changeSimTransitStockArrivals, changeLoadSimulationData } from '../../../../actions/analytics-v2'
import './ModalTransitStockArrival.css'

const ModalTransitStockArrival =
({
    modalData = {},
    disabled,
    dateRange,
    simulationTransitStockArrivals,
    changeSimTransitStockArrivals,
    loadSimulationData,
    changeLoadSimulationData,
})=>{
    const { t } = useTranslation()
    const [ open, setOpen ] = useState(false)
    const [ date, setDate ] = useState("")
    const [ quantity, setQuantity ] = useState("")
    const [ errorFields, setErrorFields ] = useState({ date: false, quantity: false })
    const [ action, setAction ] = useState(false)
    const { loaded_results="", sim_inputs="" } = loadSimulationData[0] || {}

    const existModalData = "date" in modalData
    const currentArrivals = loadSimulationData.length
        ? (sim_inputs.transit_stock_arrivals || [])
        : simulationTransitStockArrivals

    useEffect(()=>{
        if("date" in modalData){
            setDate(modalData.date)
            setQuantity(String(modalData.quantity))
        }
    }, [modalData, open])

    const calendarDateToIso = (calendarDate)=> calendarDate.split('/').reverse().join('-')
    const isoDateToDisplay = (isoDate)=> isoDate ? isoDate.split('-').reverse().join('/') : ""

    const commitArrivals = (newArrivals)=>{
        if(loadSimulationData.length){
            changeLoadSimulationData([{ loaded_results,
                                        sim_inputs: { ...sim_inputs, transit_stock_arrivals: newArrivals } }])
            return
        }
        changeSimTransitStockArrivals(newArrivals)
    }

    const addEntry = ()=>{
        const allow = allowAddTransitStockArrival(date, quantity, setErrorFields)
        if(allow){
            commitArrivals([...currentArrivals, { date, quantity: parseFloat(quantity) }])
            setDate("")
            setQuantity("")
            setAction((current)=> !current)
        }
    }

    const removeEntry = (entry)=>{
        commitArrivals(currentArrivals.filter((existingEntry)=> !_.isEqual(existingEntry, entry)))
    }

    const filterData = (typeArray)=>{
        return typeArray.map((entry)=> (_.isEqual(entry, modalData) ? { date, quantity: parseFloat(quantity) } : entry))
    }

    const editEntry = ()=>{
        const allow = allowAddTransitStockArrival(date, quantity, setErrorFields)
        if(allow){
            commitArrivals(filterData(currentArrivals))
            setErrorFields({ date: false, quantity: false })
            setOpen(false)
        }
    }

    const discardAndClose = ()=>{
        setErrorFields({ date: false, quantity: false })
        setDate("")
        setQuantity("")
        setOpen(false)
    }

    const disableAddButton = date || quantity ? false : true

    return(
        <Modal
            onClose={()=>discardAndClose()}
            onOpen={()=>setOpen(true)}
            open={open}
            trigger={
                <Button
                    fluid={!existModalData}
                    disabled={disabled}
                    positive={existModalData}
                    icon={existModalData ? "pencil" : null}
                    className={classNames({"button-color": !existModalData})}>
                        {existModalData ? null : t("simulation.addTransitStockArrival")}
                </Button>}
            >
            <Modal.Header>{t("simulation.addTransitStockArrival")}</Modal.Header>
            <Modal.Content>
                <Grid stackable>
                    <Grid.Row columns={2}>
                        <Grid.Column>
                            <CustomCalendarInput
                                title={t("simulation.transitStockArrivalDate")}
                                required={true}
                                validRange={dateRange}
                                error={errorFields.date}
                                onDateClick={(calendarDate)=>setDate(calendarDateToIso(calendarDate))}
                                action={action}
                                disabled={disabled}
                                currentDate={dateRange.startDateRange}
                                placeholder={isoDateToDisplay(date)}
                                />
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field error={errorFields.quantity} required disabled={disabled}>
                                <label className="field-title">{t("simulation.transitStockArrivalQuantity")}</label>
                                <Input
                                    disabled={disabled}
                                    className="quantity-label"
                                    value={quantity}
                                    placeholder={!quantity ? "200" : null}
                                    fluid
                                    onChange={(event, valueInserted)=>setQuantity(maskTextToExtractFloat(valueInserted.value))}
                                    error={errorFields.quantity}
                                    />
                            </Form.Field>
                        </Grid.Column>
                    </Grid.Row>
                    {
                        !existModalData
                            &&  <Grid.Row>
                                    <Grid.Column>
                                        <Button
                                            floated='right'
                                            onClick={()=>addEntry()}
                                            disabled={disableAddButton}
                                            className="add-button"
                                            >
                                                {t("simulation.addTittle")}
                                        </Button>
                                    </Grid.Column>
                                </Grid.Row>
                    }
                    {
                        !existModalData && currentArrivals.length > 0
                            &&  <Grid.Row>
                                    <Grid.Column>
                                        <Table celled compact size="small">
                                            <Table.Header>
                                                <Table.Row>
                                                    <Table.HeaderCell>{t("simulation.transitStockArrivalDate")}</Table.HeaderCell>
                                                    <Table.HeaderCell>{t("simulation.transitStockArrivalQuantity")}</Table.HeaderCell>
                                                    <Table.HeaderCell></Table.HeaderCell>
                                                </Table.Row>
                                            </Table.Header>
                                            <Table.Body>
                                                {
                                                    currentArrivals.map((entry, index)=>(
                                                        <Table.Row key={`${entry.date}-${entry.quantity}-${index}`}>
                                                            <Table.Cell>{isoDateToDisplay(entry.date)}</Table.Cell>
                                                            <Table.Cell>{entry.quantity}</Table.Cell>
                                                            <Table.Cell textAlign="right">
                                                                <Icon
                                                                    name="trash"
                                                                    color="red"
                                                                    link
                                                                    disabled={disabled}
                                                                    onClick={()=>removeEntry(entry)}
                                                                    />
                                                            </Table.Cell>
                                                        </Table.Row>
                                                    ))
                                                }
                                            </Table.Body>
                                        </Table>
                                    </Grid.Column>
                                </Grid.Row>
                    }
                </Grid>
            </Modal.Content>
            <Modal.Actions>
                {
                    existModalData
                        ?   <>
                                <Button color='black'
                                    onClick={() => discardAndClose()}>
                                    {t("cancel")}
                                </Button>
                                <Button
                                    onClick={() => editEntry()}
                                    positive
                                    >
                                    {t("confirm")}
                                </Button>
                            </>
                        :   <Button
                                onClick={() => discardAndClose()}
                                positive
                                >
                                {t("close")}
                            </Button>
                }
            </Modal.Actions>
        </Modal>
    )
}

ModalTransitStockArrival.propTypes = {
    modalData: PropTypes.object,
    disabled: PropTypes.bool,
    dateRange: PropTypes.object,
    simulationTransitStockArrivals: PropTypes.array,
    changeSimTransitStockArrivals: PropTypes.func,
    loadSimulationData: PropTypes.array,
    changeLoadSimulationData: PropTypes.func,
}

const mapStateToProps = (state)=>({
    simulationTransitStockArrivals: state.analytics.simulationTransitStockArrivals,
    loadSimulationData: state.analytics.loadSimulationData,
})

export default connect(mapStateToProps, { changeSimTransitStockArrivals, changeLoadSimulationData })(ModalTransitStockArrival)
